import { Redirect, Route, Switch, useLocation } from 'react-router'

import { ProtectedRoute } from '@webapp/app'
import { useAppRoutes, useFeatureFlag } from '@webapp/hooks'
import { OrderStatusPage } from '@webapp/pages'
import { Permissions } from '@webapp/platform/iam'
import { OrderApprovalsPage } from './OrderApprovalsPage'
import { OrderDetailsPage } from './OrderDetailsPage'
import { OrderRequestPage } from './OrderRequestPage'
import {
  OrderApprovalPageStatuses,
  OrderDetailsPageTabs,
  OrderStatusPageStatuses,
} from './types'

const orderStatusFormat = Object.values(OrderStatusPageStatuses).join('|')
const orderDetailsTabFormat = Object.values(OrderDetailsPageTabs).join('|')
const orderApprovalStatusFormat = Object.values(OrderApprovalPageStatuses).join(
  '|'
)

export const OrderRouter = () => {
  const { brp, url } = useAppRoutes()
  const location = useLocation()

  const RFO_AUTOMATION = useFeatureFlag('RFO_AUTOMATION')
  const PSERV_346_BASE_MIGRATION = useFeatureFlag('PSERV_346_BASE_MIGRATION')

  // Whatever (possibly empty) status segment follows `/orders/approvals`,
  // used by the flag-off Angular-fallback redirect below.
  const unmatchedApprovalStatus =
    location.pathname.match(/^\/orders\/approvals\/?(.*)$/)?.[1] ?? ''

  const orderDetailsPagePermissions = [
    Permissions.ADD_ORDER,
    Permissions.PROCURE_ADD_PO,
    Permissions.ACCOUNTS_PAYABLE_ADD_BILL,
    Permissions.ACCOUNTS_PAYABLE_CREATE_PAYMENTS,
    Permissions.APPROVE_ORDER,
    /**
     * @legacy
     * remove with `legacy-payments` switch
     */
    Permissions.LEGACY_PAYMENT,
  ]

  return (
    <Switch>
      {/* Order Request */}
      {RFO_AUTOMATION ? (
        <ProtectedRoute
          path={brp('OrderRequestPage')}
          component={OrderRequestPage}
          permissions={[Permissions.ADD_ORDER]}
        />
      ) : (
        <Redirect
          exact
          from={brp('OrderRequestPage')}
          to={url('LegacyOrderRequestPage')}
        />
      )}

      {/* Order Status */}
      <Redirect
        exact
        from={brp('OrderStatusPage', {
          status: '',
        })}
        to={url('OrderStatusPage', {
          status: OrderStatusPageStatuses.PENDING,
        })}
      />

      <ProtectedRoute
        path={brp('OrderStatusPage', {
          status: `:status(${orderStatusFormat})`,
        })}
        component={OrderStatusPage}
        navigationPermissions={['request__order']}
      />

      {/*
       * Order Approvals
       *
       * Route conflict — insertion order is load-bearing (HLD §4): this
       * three-part block MUST precede both the unconstrained `/orders/:uuid`
       * detail routes below and the `path='*'` catch-all — otherwise
       * `/orders/approvals` would exact-match the detail route (uuid
       * = 'approvals') and `/orders/approvals/pending` would fail the tab
       * regex and fall into the catch-all.
       */}
      {/*
       * PSERV_346: flag-off falls back to the Angular order approvals page.
       * The status is pulled directly off `location.pathname` (rather than
       * left as a `:status?` template placeholder for react-router to
       * substitute) so `location.search` can be appended unambiguously — a
       * literal `?` immediately after the optional-param `?` modifier would
       * otherwise be at the mercy of path-to-regexp's compile() parsing.
       */}
      {!PSERV_346_BASE_MIGRATION && (
        <Redirect
          from={brp('OrderApprovalsPage', { status: ':status?' })}
          to={`/#/orders/approvals${unmatchedApprovalStatus ? `/${unmatchedApprovalStatus}` : ''}${location.search}`}
        />
      )}

      <ProtectedRoute
        path={brp('OrderApprovalsPage', {
          status: `:status(${orderApprovalStatusFormat})`,
        })}
        component={OrderApprovalsPage}
        permissions={[Permissions.APPROVE_ORDER]}
        featureFlags={['PSERV_346_BASE_MIGRATION']}
      />

      {/*
       * Empty or unknown status falls through the constrained route above
       * and redirects to the default pending tab. The `from` prop is
       * load-bearing: a from-less <Redirect> inside a <Switch> matches EVERY
       * location, which would black-hole the entire orders router.
       * `location.search` is appended for the same deep-link-preservation
       * reason as the flag-off branch above.
       */}
      {PSERV_346_BASE_MIGRATION && (
        <Redirect
          from={brp('OrderApprovalsPage', { status: ':status?' })}
          to={`${url('OrderApprovalsPage', { status: 'pending' })}${location.search}`}
        />
      )}

      {/* Order Details */}
      <ProtectedRoute
        exact
        path={brp('OrderDetailsPage', { uuid: ':uuid' })}
        component={OrderDetailsPage}
        permissions={orderDetailsPagePermissions}
      />
      <ProtectedRoute
        exact
        path={brp('OrderDetailsPage', {
          uuid: ':uuid',
          tab: `:tab(${orderDetailsTabFormat})`,
        })}
        component={OrderDetailsPage}
        permissions={orderDetailsPagePermissions}
      />

      {/* Fallback for /orders/ */}
      <Route
        path='*'
        render={() => (
          <Redirect
            to={url('OrderStatusPage', {
              status: OrderStatusPageStatuses.PENDING,
            })}
          />
        )}
      />
    </Switch>
  )
}
